/**
 * Helpers de presentación para la vista cliente de una minuta.
 *
 * Modelo:
 *   - COLACIÓN PRINCIPAL (id = 3), HIPOCALÓRICO (id = 5),
 *     DIETA BLANDA (id = 6), ALMUERZO SIN GLUTEN (id = 7) y
 *     CENA (id = 10) se asignan en dos filas dentro de
 *     minutas_detalladas (modelo Fase E, ver
 *     db/migrations/2026-04-26_modelo_opciones_minuta_con_rol.sql):
 *       rol = 'fondo'      → obligatorio
 *       rol = 'guarnicion' → opcional
 *   - Cuando se renderiza para el cliente final (menú impresible, vista
 *     pública, PDF, etc.), el chef quiere ver una sola línea:
 *       "<Fondo> con <Guarnición>"   si hay ambos
 *       "<Fondo>"                     si solo hay fondo
 *
 * Convención documentada en
 *   db/migrations/2026-04-26_modelo_fondo_guarnicion_tags.sql
 *   db/migrations/2026-05-12_dieta_blanda_combinado.sql
 *   db/migrations/2026-05-12_almuerzo_sin_gluten_combinado.sql
 *   db/migrations/2026-05-18_hipocalorico_combinado.sql
 *
 * Estos helpers reciben los nombres de receta (no objetos) para minimizar
 * acoplamiento. El consumidor extrae los nombres de su estructura local.
 *
 * NOTA: este archivo es la ÚNICA fuente de verdad en el frontend para
 * "este tipo es combinado". Ver gemelo backend en
 * packages/backend/src/services/minutaModel.ts. No duplicar la lista
 * de ids en otros componentes.
 */

/**
 * Componer la cadena "<Fondo> con <Guarnición>" o "<Fondo>" para una celda
 * de COLACIÓN PRINCIPAL / CENA.
 *
 * @param fondo        Nombre de la receta de fondo (fila_orden=1).
 *                     Puede ser string vacío o null si la celda está vacía.
 * @param guarnicion   Nombre de la receta de guarnición (fila_orden=2).
 *                     null/undefined/'' si no se asignó.
 * @returns            Cadena formateada o '' si no hay fondo.
 */
export function formatColacionConGuarnicion(
  fondo: string | null | undefined,
  guarnicion: string | null | undefined
): string {
  const f = (fondo ?? '').trim();
  const g = (guarnicion ?? '').trim();
  if (!f) return '';
  if (!g) return f;
  return `${f} con ${g}`;
}

/**
 * ¿Es un tipo de plato que admite la convención fondo + guarnición?
 * Hoy: COLACIÓN PRINCIPAL (3), HIPOCALÓRICO (5), DIETA BLANDA (6),
 * ALMUERZO SIN GLUTEN (7), CENA (10).
 *
 * Si en el futuro se agrega otro tipo combinado, basta extender este
 * Set. Ver gemelo backend en services/minutaModel.ts. Con 5 ids
 * hardcoded estamos en el límite cómodo; al 6to vale la pena
 * promover a columna `tipos_plato.es_combinado` configurable desde
 * BD (TD-013).
 */
const TIPOS_COMBINADOS: ReadonlySet<number> = new Set([3, 5, 6, 7, 10]);

export function tipoPlatoUsaFondoGuarnicion(tipoPlatoId: number): boolean {
  return TIPOS_COMBINADOS.has(tipoPlatoId);
}
